<script setup lang="ts">
import type { PnUploadFileItem } from "@postnord/web-components";
import { ref } from "vue";
import type { StickerUpload } from "@/types/stickers";

const props = defineProps<{
  open: boolean;
  showLoader?: boolean;
}>();

const emit = defineEmits<{
  (e: "onClose"): void;
  (e: "onSave", stickers: StickerUpload[]): void;
}>();

const filesToUpload = ref<PnUploadFileItem[]>([]);
const hasUploadError = ref(false);
const errorMessage = ref("");
const fileUploadKey = ref(0);

const mapUploads = (): StickerUpload[] => {
  return filesToUpload.value.map((file) => ({
    filename: file.filename,
    base64: file.base64,
    categories: [],
    completed: false,
  }));
};

const reset = () => {
  hasUploadError.value = false;
  errorMessage.value = "";
  filesToUpload.value = [];
  fileUploadKey.value += 1;
};

const onClose = () => {
  reset();
  emit("onClose");
};

const filesAdded = (e: any) => {
  errorMessage.value = "";
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);
    if (errorFiles.length > 0) {
      hasUploadError.value = true;
      errorMessage.value =
        "Please upload valid files within the allowed format and size.";
      return;
    }
    hasUploadError.value = false;
    filesToUpload.value = e.detail;
    return;
  }

  hasUploadError.value = false;
  filesToUpload.value = [];
};

const onSave = () => {
  if (filesToUpload.value.length === 0) {
    errorMessage.value = "You must upload at least one file.";
    return;
  }

  if (hasUploadError.value) {
    return;
  }

  errorMessage.value = "";
  const uploads = mapUploads();
  reset();
  emit("onSave", uploads);
};
</script>

<template>
  <pn-modal :open="props.open" @close="onClose" class="add-stickers-modal">
    <div :class="[{ spinner: props.showLoader }]">
      <pn-spinner size="3" v-show="props.showLoader" />
    </div>
    <h2 class="pn-l margin-0">Add stickers</h2>
    <p class="pn-s helper-text">Add one or multiple stickers to collection.</p>

    <div class="upload-wrapper">
      <pn-file-upload
        :key="fileUploadKey"
        accept=".svg,.jpg, .png"
        max-size="4mb"
        limit="20"
        @filesAdded="filesAdded"
      ></pn-file-upload>
    </div>

    <p class="pn-s upload-error" v-if="errorMessage">
      {{ errorMessage }}
    </p>

    <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
    <div slot="buttons">
      <div class="button-row">
        <pn-button appearance="light" variant="outlined" @click="onClose"
          >Cancel</pn-button
        >
        <pn-button @click="onSave">Save</pn-button>
      </div>
    </div>
  </pn-modal>
</template>

<style lang="scss" scoped>
pn-modal .pn-modal {
  width: 632px !important;
  max-width: 640px !important;
}
.helper-text {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.upload-wrapper {
  width: 100%;
}

.upload-error {
  margin-top: 0.75rem;
  color: #c00;
}

.button-row {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

pn-modal .pn-modal > .pn-modal-content {
  padding: 24px !important;
}

pn-modal .pn-modal > [slot="buttons"] {
  padding: 24px;
}

.spinner-no-background {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
</style>
